import { useState, useEffect, useRef } from 'react';
import Header from './components/shared/Header';
import ProgressBar from './components/shared/ProgressBar';
import Step1FormSaisie from './components/Step1-FormSaisie';
import Step2Resultats from './components/Step2-Resultats';
import Step3ProtectionSociale from './components/Step3-ProtectionSociale';
import Step4CallToAction from './components/Step4-CallToAction';
import { calculateCAE } from './utils/calculations/calculateCAE';
import { calculateMicroEntreprise } from './utils/calculations/calculateMicro';
import { calculateEI } from './utils/calculations/calculateEI';
import { calculateSASU } from './utils/calculations/calculateSASU';

function App() {
  // ==================== ÉTAT GLOBAL ====================
  const [currentStep, setCurrentStep] = useState(1);
  const [config, setConfig] = useState(null);
  const [simulationData, setSimulationData] = useState({
    // Données de l'étape 1
    metier: '',
    caHT: 0,
    caTTC: 0,
    isTTC: false,
    chargesProfessionnelles: {
      matieres_premieres: 0,
      sous_traitance: 0,
      loyer: 0,
      internet_telephone: 0,
      compte_bancaire: 0,
      indemnites_km: 0,
      deplacements: 0,
      logiciel_compta: 0,
      communication_pub: 0,
      consommables: 0,
      repas_affaires: 0,
      assurance_rcp: 0,
      assurance_decennale: 0,
      comptable: 0,
      cet_cvae_cfe: 0,
      mutuelle_prevoyance: 0,
      label_bio: 0,
      code_barres: 0,
      logiciel_formation: 0,
    },
    chargesProfessionnellesHorsCAE: null,
    totalCharges: 0,

    // Données de calcul (étape 2)
    resultatCAE: null,
    resultatMicro: null,
    resultatEI: null,
    resultatSASU: null,

    // Statut sélectionné pour comparaison
    statutComparaison: 'micro', // 'micro', 'ei', ou 'sasu'
  });

  // ==================== CHARGEMENT CONFIG CAE ====================
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const response = await fetch('/config/config.json');
        const configData = await response.json();
        setConfig(configData);
      } catch (error) {
        console.error('Erreur chargement configuration:', error);
        // Config par défaut en cas d'erreur
        setConfig({
          parametres_cae: {
            contribution_pourcentage: 14,
            forfait_rcp_cae_batiment: 20,
            tx_decennale_batiment: 0.03,
            forfait_rcp_cae_hors_batiment: 15,
            provision_interessement_pourcentage: 3.15,
            charges_forfaitaires_mutuelles: 84,
            charges_forfaitaires_annuel_medecine_travail: 95,
            cotisation_fede_urscop: 12.594,
          },
        });
      }
    };
    loadConfig();
  }, []);

  // ==================== CALCULS DES RÉSULTATS ====================
  const [resultats, setResultats] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const inputSignatureRef = useRef(null);

  // L'étape 2 n'affiche que CAE + le statut comparé ; l'étape 4 (impression) a besoin des 4.
  const needsAllStatuts = currentStep >= 4;

  useEffect(() => {
    if (!simulationData.caHT || !config) return;

    const chargesProf = simulationData.chargesProfessionnelles;
    const chargesProfHorsCAE = simulationData.chargesProfessionnellesHorsCAE ?? chargesProf;

    // On ne calcule que les statuts réellement affichés (limitation des appels API / 429) :
    // CAE + statut comparé en étapes 1-3, les 4 à l'impression.
    const computeStatut = (statut) => {
      switch (statut) {
        case 'cae':
          return calculateCAE(simulationData.caHT, simulationData.isTTC, chargesProf, config.parametres_cae, simulationData.metier)
            .then((r) => ['resultatCAE', r]);
        case 'micro':
          return calculateMicroEntreprise(simulationData.caHT, simulationData.isTTC, simulationData.metier, chargesProfHorsCAE, config.metiers)
            .then((r) => ['resultatMicro', r]);
        case 'ei':
          return calculateEI(simulationData.caHT, simulationData.isTTC, chargesProfHorsCAE, simulationData.metier)
            .then((r) => ['resultatEI', r]);
        case 'sasu':
          return calculateSASU(simulationData.caHT, simulationData.isTTC, chargesProfHorsCAE, simulationData.metier)
            .then((r) => ['resultatSASU', r]);
        default:
          return Promise.resolve(null);
      }
    };

    const statutsRequis = needsAllStatuts
      ? ['cae', 'micro', 'ei', 'sasu']
      : ['cae', simulationData.statutComparaison || 'micro'];

    // Quand les entrées changent, les anciens résultats sont périmés → on repart de zéro.
    // Quand seul le statut affiché (ou l'étape) change, on fusionne pour conserver l'existant.
    const signature = JSON.stringify({
      caHT: simulationData.caHT,
      isTTC: simulationData.isTTC,
      metier: simulationData.metier,
      chargesProf,
      chargesProfHorsCAE,
    });
    const isNewInput = signature !== inputSignatureRef.current;
    inputSignatureRef.current = signature;

    // Debounce uniquement pour la frappe des entrées ; instantané sur un simple changement de statut/étape.
    const delai = isNewInput ? 800 : 0;

    setIsCalculating(true);
    const timer = setTimeout(() => {
      Promise.all(statutsRequis.map(computeStatut))
        .then((entries) => {
          const obj = Object.fromEntries(entries.filter(Boolean));
          setResultats((prev) => (isNewInput || !prev ? obj : { ...prev, ...obj }));
        })
        .catch((error) => {
          console.error('Erreur calcul (API mon-entreprise):', error);
        })
        .finally(() => {
          setIsCalculating(false);
        });
    }, delai);

    return () => {
      clearTimeout(timer);
      setIsCalculating(false);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [simulationData.caHT, simulationData.isTTC, simulationData.metier, JSON.stringify(simulationData.chargesProfessionnelles), JSON.stringify(simulationData.chargesProfessionnellesHorsCAE), simulationData.statutComparaison, needsAllStatuts, config]);

  // Sélectionner le résultat de comparaison selon le statut
  const getResultatComparaison = () => {
    if (!resultats) return null;
    
    switch (simulationData.statutComparaison) {
      case 'micro':
        return resultats.resultatMicro;
      case 'ei':
        return resultats.resultatEI;
      case 'sasu':
        return resultats.resultatSASU;
      default:
        return resultats.resultatMicro;
    }
  };

  // ==================== CHANGEMENT DE STATUT ====================
  const handleChangeStatut = (nouveauStatut) => {
    setSimulationData(prev => ({
      ...prev,
      statutComparaison: nouveauStatut,
    }));
  };

  // ==================== SCROLL EN HAUT À CHAQUE CHANGEMENT D'ÉTAPE ====================
  useEffect(() => {
    if (
      typeof window === 'undefined' ||
      typeof document === 'undefined' ||
      !document.documentElement
    ) {
      return;
    }

    const htmlElement = document.documentElement;
    const previousBehavior = htmlElement.style.scrollBehavior;

    htmlElement.style.scrollBehavior = 'auto';
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });

    htmlElement.scrollTop = 0;
    if (document.body) {
      document.body.scrollTop = 0;
    }

    const restoreBehavior = () => {
      htmlElement.style.scrollBehavior = previousBehavior;
    };

    let rafId = null;
    let timeoutId = null;

    if (typeof window.requestAnimationFrame === 'function') {
      rafId = window.requestAnimationFrame(restoreBehavior);
    } else {
      timeoutId = window.setTimeout(restoreBehavior, 0);
    }

    return () => {
      if (rafId !== null && typeof window.cancelAnimationFrame === 'function') {
        window.cancelAnimationFrame(rafId);
      }
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }
      htmlElement.style.scrollBehavior = previousBehavior;
    };
  }, [currentStep]);

  // ==================== NAVIGATION ENTRE ÉTAPES ====================
  const goToNextStep = () => {
    if (currentStep < 4) setCurrentStep(currentStep + 1);
  };

  const goToPreviousStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const goToStep = (step) => {
    if (step >= 1 && step <= 4) setCurrentStep(step);
  };

  const resetSimulation = () => {
    setCurrentStep(1);
    setSimulationData({
      metier: '',
      caHT: 0,
      caTTC: 0,
      isTTC: false,
      chargesProfessionnelles: {
        matieres_premieres: 0,
        sous_traitance: 0,
        loyer: 0,
        internet_telephone: 0,
        compte_bancaire: 0,
        indemnites_km: 0,
        deplacements: 0,
        logiciel_compta: 0,
        communication_pub: 0,
        consommables: 0,
        repas_affaires: 0,
        assurance_rcp: 0,
        assurance_decennale: 0,
        comptable: 0,
        cet_cvae_cfe: 0,
        mutuelle_prevoyance: 0,
        label_bio: 0,
        code_barres: 0,
        logiciel_formation: 0,
      },
      chargesProfessionnellesHorsCAE: null,
      totalCharges: 0,
      resultatCAE: null,
      resultatMicro: null,
      resultatEI: null,
      resultatSASU: null,
      statutComparaison: 'micro',
    });
  };

  // ==================== MISE À JOUR DES DONNÉES ====================
  const updateSimulationData = (newData) => {
    setSimulationData((prev) => ({
      ...prev,
      ...newData,
    }));
  };

  // ==================== RENDU DES ÉTAPES ====================
  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <Step1FormSaisie
            data={simulationData}
            updateData={updateSimulationData}
            onNext={goToNextStep}
          />
        );
      case 2:
        return resultats && resultats.resultatCAE ? (
          <Step2Resultats
            data={simulationData}
            resultatCAE={resultats.resultatCAE}
            resultatMicro={resultats.resultatMicro}
            resultatEI={resultats.resultatEI}
            resultatSASU={resultats.resultatSASU}
            resultatComparaison={getResultatComparaison()}
            statutComparaison={simulationData.statutComparaison}
            onChangeStatut={handleChangeStatut}
            onNext={goToNextStep}
            onPrevious={goToPreviousStep}
            onPrint={() => goToStep(4)}
          />
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-600">
              {isCalculating ? 'Calcul en cours (API salaires)...' : 'Chargement des résultats...'}
            </p>
          </div>
        );
      case 3:
        return (
          <Step3ProtectionSociale
            data={simulationData}
            onNext={goToNextStep}
            onPrevious={goToPreviousStep}
          />
        );
      case 4:
        return resultats && resultats.resultatCAE && resultats.resultatMicro && resultats.resultatEI && resultats.resultatSASU ? (
          <Step4CallToAction
            data={simulationData}
            resultats={resultats}
            configCAE={config}
            onPrevious={goToPreviousStep}
            onReset={resetSimulation}
          />
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-600">Préparation du document…</p>
          </div>
        );
      default:
        return null;
    }
  };

  // ==================== RENDU PRINCIPAL ====================
  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      {/* Header + barre de progression — bloc sticky unique */}
      <div className="sticky top-0 z-50 print:hidden">
        <Header />
        <ProgressBar currentStep={currentStep} totalSteps={4} onStepClick={goToStep} />
      </div>

      {/* Contenu principal */}
      <main className="flex-grow container mx-auto px-4 py-8 max-w-6xl">
        <div className="animate-fade-in">
          {renderStep()}
        </div>
      </main>

    </div>
  );
}

export default App;
